// Kontakta ej: a contact who asked not to be contacted again (GDPR). Pure
// rules, kept apart from the components so the gates every surface shares can
// be checked directly.
import { fmtDate } from '@/shared/lib/format'
import type { Me } from '@/shared/api/session'
import type { DoNotContact, Lead } from '@/shared/types'

/** The `detail` the server answers with when it refuses a write on a marked lead. */
export const DO_NOT_CONTACT_DETAIL = 'Kontakta ej'

/** The server caps the reason at 500 characters. */
export const DO_NOT_CONTACT_REASON_MAX = 500

// Mirrors EDIT_LEAD_PERMISSIONS in app/api/shared/permissions.py, the gate on
// POST /leads/{id}/do-not-contact.
const EDIT_LEAD_PERMISSIONS = ['change_stage', 'log_calls', 'manage_notes', 'edit_contacts']

// The letters Focus and the overlay bind to an outcome or to Postpone.
const OUTCOME_SHORTCUT_KEYS: ReadonlySet<string> = new Set(['r', 'n', 'b', 'x', 'c', 'w', 'e', 'p'])

export function isDoNotContact(lead: Pick<Lead, 'doNotContact'> | null | undefined): boolean {
  return !!lead?.doNotContact
}

export type LeadActions = { kind: 'outcomes' } | { kind: 'doNotContact'; mark: DoNotContact }

/** What the action bar shows: the outcome buttons, or the Kontakta ej notice in their place. */
export function leadActions(lead: Pick<Lead, 'doNotContact'>): LeadActions {
  return lead.doNotContact ? { kind: 'doNotContact', mark: lead.doNotContact } : { kind: 'outcomes' }
}

/** True when `key` would open an outcome or postpone a lead that may not be contacted. */
export function blocksOutcomeShortcut(lead: Pick<Lead, 'doNotContact'> | null | undefined, key: string): boolean {
  return isDoNotContact(lead) && OUTCOME_SHORTCUT_KEYS.has(key.toLowerCase())
}

export function canMarkDoNotContact(me: Me | undefined): boolean {
  return EDIT_LEAD_PERMISSIONS.some(p => !!me?.permissions?.includes(p))
}

/** Only user administrators may lift the mark; DELETE answers 403 to anyone else. */
export function canRemoveDoNotContact(me: Me | undefined): boolean {
  return !!me?.permissions?.includes('manage_users')
}

/** Read structurally rather than through `instanceof ApiError`, so this module
 *  stays importable without the API client and its runtime config. */
export function isDoNotContactRefusal(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { status?: unknown; message?: unknown }
  return e.status === 409 && e.message === DO_NOT_CONTACT_DETAIL
}

/** "Markerad 22 okt. av Anna". `name` resolves the marker's email for display. */
export function markedByLine(dnc: DoNotContact, name: (email: string) => string): string {
  return `Markerad ${fmtDate(dnc.at)} av ${name(dnc.by)}`
}

/** A blank reason goes to the server as absent, not as an empty string. */
export function reasonOrUndefined(reason: string): string | undefined {
  const trimmed = reason.trim()
  return trimmed ? trimmed.slice(0, DO_NOT_CONTACT_REASON_MAX) : undefined
}
